import React, {
  forwardRef,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import Button from '@material-ui/core/Button';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { SnackbarContext, SnackbarVariant } from '@eas/common-web';
import { useWorkOrderStatesList } from '@modules/work-order-state/work-order-state-api';
import { WebsocketCacheControlContext } from '@composite/websocket/websocket-cache-control-context';
import { WorkOrder, WorkOrderState } from '@models';
import { workOrderBulkApi } from './bulk-operations-api';

export const useStyles = makeStyles((theme) => ({
  moreIcon: {
    color: theme.palette.grey[500],
    display: 'flex',
    fontSize: '16px',
  },
  bullet: {
    width: 12,
    height: 12,
    borderRadius: 12,
    marginRight: 8,
    marginLeft: 14,
    flexShrink: 0,
  },
}));

const ChangeStateMenuItem = forwardRef<
  HTMLLIElement,
  {
    submitCallback?: () => void;
    color?: string;
    label: string;

    onClick?: () => void;
  }
>(function ChangeStateMenuItem({ color, label, onClick }, ref) {
  const classes = useStyles();

  return (
    <MenuItem ref={ref} onClick={onClick}>
      {color && (
        <ListItemIcon>
          <div
            className={classes.bullet}
            style={{
              backgroundColor: color,
            }}
          />
        </ListItemIcon>
      )}
      {label}
    </MenuItem>
  );
});

function getColor(workOrderStates: WorkOrderState[], code: string) {
  return workOrderStates.find((state) => state.code === code)?.color;
}

function hasState(codes: string[], workOrders: WorkOrder[]) {
  return workOrders.every((wo) => codes.includes(wo?.currentState?.code || ''));
}

export const BulkChangeStateButton = ({
  workOrders,
  afterSubmit,
  setLoading,
  disabled,
}: {
  workOrders: WorkOrder[];
  afterSubmit: () => void;
  setLoading: (loading: boolean) => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  const validStates = getValidStates(workOrders);
  return (
    <div>
      <Button
        onClick={() => setOpen(true)}
        color="primary"
        variant="contained"
        disabled={
          validStates.length === 0 || workOrders.length === 0 || disabled
        }
        style={{ fontSize: '12px', padding: '4px 8px' }}
      >
        Zmena stavu
      </Button>
      <div style={{ visibility: 'hidden' }} ref={anchor} />
      <Menu
        anchorEl={anchor.current}
        open={open}
        onClose={() => setOpen(false)}
      >
        <BulkChangeStateMenuList
          setLoading={setLoading}
          workOrders={workOrders}
          setOpen={setOpen}
          closeMenu={() => setOpen(false)}
          afterSubmit={afterSubmit}
        />
      </Menu>
    </div>
  );
};

export const BulkChangeStateMenuList = ({
  workOrders,
  setOpen,
  closeMenu,
  afterSubmit,
  setLoading,
}: {
  workOrders: WorkOrder[];
  setOpen: (open: boolean) => void;
  closeMenu: () => void;
  afterSubmit?: () => void;
  setLoading: (loading: boolean) => void;
}) => {
  const { showSnackbar } = useContext(SnackbarContext);
  const wsCache = useContext(WebsocketCacheControlContext);
  const clearWSCache = wsCache?.['table']?.current;

  const { changeState } = workOrderBulkApi;
  const [workOrderStates] = useWorkOrderStatesList();

  const onSubmit = useCallback(
    async (code: string) => {
      setLoading?.(true);
      const response = await changeState(
        workOrders.map((wo) => wo.id),
        code
      );
      setLoading?.(false);
      const failed =
        response
          .filter((r) => !r.success)
          ?.map((r) => workOrders.find((wo) => wo.id === r.id)?.workOrderId) ??
        '';

      if (failed.length > 0) {
        showSnackbar(
          `Niektorým prac. príkazom sa nepodarilo zmeniť stav: ${failed.join(
            ', '
          )}
          `,
          SnackbarVariant.WARNING
        );
      }
      setOpen(false);
      closeMenu();

      clearWSCache?.();
      afterSubmit?.();
    },
    [
      afterSubmit,
      changeState,
      clearWSCache,
      closeMenu,
      setLoading,
      setOpen,
      showSnackbar,
      workOrders,
    ]
  );

  const validStates = getValidStates(workOrders);

  if (validStates.length === 0) {
    return (
      <MenuList disablePadding={true}>
        <MenuItem style={{ cursor: `not-allowed` }}>
          <div>
            <b>Žiadne možnosti</b>
            <div>
              Označené pracovné príkazy nie je možné hromadne prepnúť do jedného
              stavu.
            </div>
            <div>Prosím upravte výber pracovných príkazov.</div>
          </div>
        </MenuItem>
      </MenuList>
    );
  }

  return (
    <MenuList disablePadding={true}>
      {validStates.map((state) => (
        <ChangeStateMenuItem
          key={state}
          label={stateLabel[state] ?? ''}
          color={getColor(workOrderStates?.items ?? [], state)}
          onClick={() => {
            onSubmit(state);
          }}
        />
      ))}
    </MenuList>
  );
};

const stateLabel: Record<string, string> = {
  NOV: 'Nová',
  DOR: 'Doručená',
  ODM: 'Odmietnutá',
  PRI: 'Prijatá',
  POZ: 'Pozastavená',
  CES: 'Cesta',
  VYK: 'Vykonávaná',
  ODL: 'Odložená na neskôr',
  VYB: 'Vybavená',
  ZRU: 'Zrušená',
};

const getValidStates = (workOrders: WorkOrder[]): string[] => {
  const result: string[] = [];

  if (hasState(['NAP', 'ODL', 'ODM', 'ZRU'], workOrders)) {
    result.push('NOV');
  }
  if (hasState(['NAP'], workOrders)) {
    result.push('DOR');
  }
  if (hasState(['DOR'], workOrders)) {
    result.push('ODM');
    result.push('PRI');
  }

  if (hasState(['CES', 'VYK'], workOrders)) {
    result.push('POZ');
  }
  if (hasState(['PRI', 'POZ'], workOrders)) {
    result.push('CES');
  }
  if (hasState(['PRI', 'CES'], workOrders)) {
    result.push('VYK');
  }
  if (hasState(['POZ', 'PRI', 'CES', 'VYK'], workOrders)) {
    result.push('ODL');
  }
  if (hasState(['VYK'], workOrders)) {
    result.push('VYB');
  }
  if (hasState(['NOV', 'NAP', 'ODM', 'ODL'], workOrders)) {
    result.push('ZRU');
  }

  return result;
};
